import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import useAssignmentAutosave from 'hooks/useAssignmentAutosave';
import useAssignmentDerivedDataCleanup from 'hooks/useAssignmentDerivedDataCleanup';
import useAssignmentEditorCommands from 'hooks/useAssignmentEditorCommands';
import useAssignmentEditorDialogs from 'hooks/useAssignmentEditorDialogs';
import useAssignmentEditorErrorReconciliation from 'hooks/useAssignmentEditorErrorReconciliation';
import useAssignmentEditorNavigation from 'hooks/useAssignmentEditorNavigation';
import useAssignmentEditorSession from 'hooks/useAssignmentEditorSession';
import useAssignmentEditorValidation from 'hooks/useAssignmentEditorValidation';
import useAssignmentLmsReconciliation from 'hooks/useAssignmentLmsReconciliation';
import useAssignmentPersistence from 'hooks/useAssignmentPersistence';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { ASSIGNMENT_PERSISTENCE_INTENT } from 'utils/assignmentPersistencePolicy';
import { getAssignmentPublishErrors } from 'utils/assignmentSettingsValidation';
import { AUTOSAVE_STATUS } from 'utils/autosave';

const PageContext = createContext();
const getAssignmentSavedAt = (result) => result?.assignment?.modified_on;

const AssignmentEditorProvider = (props) => {
    const { auth } = useAuth();
    const {
        course,
        setAssignments,
        currentAssignment,
        batch,
        submissions,
        previewSubmissions,
        setPreviewSubmissions,
        setStudentSubmissions,
        isGradingFeatureDisabled,
        isFeedbackFeatureDisabled,
        isInlineCommentsFeatureDisabled,
    } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const history = useHistory();
    const location = useLocation();
    const isNewRoute = location.pathname.endsWith('/new');
    const courseId = course?.id || course?.course_id;
    const [isSaving, setIsSaving] = useState(false);
    const [hasFormChanges, setHasFormChanges] = useState(false);

    const session = useAssignmentEditorSession({
        auth,
        course,
        courseId,
        currentAssignment,
        importedAssignment: location.state?.assignment,
        isNewRoute,
        relatedDataReady: Boolean(submissions),
    });
    const {
        creationIdRef,
        errors,
        form,
        hydrationFormRef,
        hydrationLastSavedAtRef,
        hydrationRevision,
        importedDraft,
        isEditorReady,
        markPersisted,
        persistedAssignment,
        persistedAssignmentRef,
        preferencesReady,
        setErrors,
        setForm,
        setImportedDraft,
        setPersistedAssignment,
    } = session;

    const dialogs = useAssignmentEditorDialogs();
    const derivedDataCleanup = useAssignmentDerivedDataCleanup({
        previewSubmissions,
        setPreviewSubmissions,
        setStudentSubmissions,
    });
    const reconcileLms = useAssignmentLmsReconciliation({
        auth,
        form,
        persistedAssignment,
        setDialog,
        closeDialog,
        setPushRubricDialog: dialogs.setPushRubricDialog,
    });
    const updateForm = useCallback(
        (updater) => {
            if (isNewRoute) setHasFormChanges(true);
            setForm((current) => (typeof updater === 'function' ? updater(current) : updater));
        },
        [isNewRoute, setForm]
    );
    const validateForPublish = useAssignmentEditorValidation({
        form,
        setErrors,
        isGradingFeatureDisabled,
        isFeedbackFeatureDisabled,
        isInlineCommentsFeatureDisabled,
    });
    const restrictions = useMemo(
        () => ({ isGradingFeatureDisabled, isFeedbackFeatureDisabled, isInlineCommentsFeatureDisabled }),
        [isFeedbackFeatureDisabled, isGradingFeatureDisabled, isInlineCommentsFeatureDisabled]
    );
    useAssignmentEditorErrorReconciliation({ form, restrictions, setErrors });

    const setAssignmentInApp = useCallback(
        (saved) => {
            setAssignments((current) => {
                const assignments = current || [];
                const exists = assignments.some(({ id }) => id === saved.id);
                return exists
                    ? assignments.map((item) => (item.id === saved.id ? saved : item))
                    : [...assignments, saved];
            });
        },
        [setAssignments]
    );
    const removeAssignmentInApp = useCallback(
        (id) => setAssignments((current) => (current || []).filter((assignment) => assignment.id !== id)),
        [setAssignments]
    );

    const { deleteAssignment, persist } = useAssignmentPersistence({
        auth,
        courseId,
        creationIdRef,
        persistedAssignmentRef,
        persistedAssignment,
        restrictions,
        setPersistedAssignment,
        setAssignmentInApp,
        removeAssignmentInApp,
        derivedDataCleanup,
        onPersisted: markPersisted,
    });

    const autosaveValid = useMemo(
        () =>
            Boolean(persistedAssignment?.is_draft || !persistedAssignment) ||
            Boolean(!Object.keys(getAssignmentPublishErrors(form || {}, restrictions)).length),
        [form, persistedAssignment, restrictions]
    );
    const persistAutosave = useCallback(
        (snapshot) => persist({ snapshot, intent: ASSIGNMENT_PERSISTENCE_INTENT.AUTOSAVE }),
        [persist]
    );
    const autosave = useAssignmentAutosave({
        enabled: isNewRoute ? preferencesReady : Boolean(form && persistedAssignment),
        autoSaveEnabled: isNewRoute
            ? preferencesReady && (hasFormChanges || importedDraft)
            : Boolean(form && persistedAssignment && !batch?.length && !submissions?.length),
        form,
        initialLastSavedAt: persistedAssignment?.modified_on,
        isValid: autosaveValid,
        onInvalidSave: validateForPublish,
        getSavedAt: getAssignmentSavedAt,
        persist: persistAutosave,
    });
    const resetAutosave = autosave.reset;
    const markAutosaveDirty = autosave.markDirty;
    const currentAssignmentId = currentAssignment?.id;

    useEffect(() => {
        if (!isNewRoute && currentAssignmentId) {
            resetAutosave(hydrationFormRef.current, hydrationLastSavedAtRef.current);
        }
    }, [currentAssignmentId, hydrationFormRef, hydrationLastSavedAtRef, hydrationRevision, isNewRoute, resetAutosave]);

    useEffect(() => {
        if (isNewRoute) setHasFormChanges(false);
    }, [hydrationRevision, isNewRoute]);

    useEffect(() => {
        if (importedDraft && preferencesReady) {
            setHasFormChanges(true);
            markAutosaveDirty();
            setImportedDraft(false);
        }
    }, [importedDraft, markAutosaveDirty, preferencesReady, setImportedDraft]);

    useEffect(() => {
        if (isNewRoute && persistedAssignment?.id && autosave.status === AUTOSAVE_STATUS.SAVED && !autosave.isDirty) {
            history.replace(`/assignments/${persistedAssignment.id}/edit`);
        }
    }, [autosave.isDirty, autosave.status, history, isNewRoute, persistedAssignment]);

    const onClickBack = useAssignmentEditorNavigation({
        isDirty: autosave.isDirty,
        isSaving: autosave.status === AUTOSAVE_STATUS.SAVING || isSaving,
        backPath: isNewRoute ? '/assignments' : `/assignments/${persistedAssignment?.id || currentAssignment?.id}`,
    });

    const { onClickCancel, onClickDelete, onClickPublish, onClickSave } = useAssignmentEditorCommands({
        autosave,
        closeDialog,
        deleteAssignment,
        form,
        history,
        isNewRoute,
        message,
        persistedAssignment,
        persistedAssignmentRef,
        persist,
        reconcileLms,
        setDialog,
        setIsSaving,
        t,
        validateForPublish,
    });

    const contextValue = useMemo(
        () => ({
            form,
            setForm: updateForm,
            errors,
            setErrors,
            ...dialogs,
            isSaving,
            setIsSaving,
            autosaveStatus: autosave.status,
            lastSavedAt: autosave.lastSavedAt,
            resumeAutosave: autosave.resume,
            isEditorReady,
            isPersisted: Boolean(persistedAssignment?.id),
            isDraft: isNewRoute || Boolean(persistedAssignment?.is_draft),
            onClickDelete,
            onClickBack,
            onClickCancel,
            onClickSave,
            onClickPublish,
        }),
        [
            autosave.lastSavedAt,
            autosave.resume,
            autosave.status,
            dialogs,
            errors,
            form,
            isEditorReady,
            isNewRoute,
            isSaving,
            onClickBack,
            onClickCancel,
            onClickDelete,
            onClickPublish,
            onClickSave,
            persistedAssignment,
            setErrors,
            updateForm,
        ]
    );

    return <PageContext.Provider value={contextValue} {...props} />;
};

export { PageContext };
export default AssignmentEditorProvider;
